/**
 * Regeneration Service
 * Handles credit preview and regeneration API calls
 * Modularized from App.js for better maintainability
 */

import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Fetch full campaign data with all product fields
 * Use this before regeneration to ensure all fields are present
 * @param {string} campaignId - The campaign ID
 * @param {string} sessionToken - User's session token
 * @returns {Promise<Object>} Full campaign data
 */
export const fetchFullCampaignData = async (campaignId, sessionToken) => {
  try {
    const response = await axios.get(
      `${API}/campaigns/${campaignId}`,
      { headers: { Authorization: `Bearer ${sessionToken}` } }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching full campaign data:', error);
    throw error;
  }
};

/**
 * Preview the credit cost for regenerating a single ad
 * @param {string} campaignId - The campaign ID
 * @param {string} adType - The ad type to regenerate
 * @param {string} sessionToken - User's session token
 * @returns {Promise<Object>} Credit preview data
 */
export const previewRegenerateCost = async (campaignId, adType, sessionToken) => {
  try {
    const response = await axios.post(
      `${API}/campaigns/${campaignId}/preview-regenerate-cost`,
      { ad_type: adType },
      { headers: { Authorization: `Bearer ${sessionToken}` } }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching regenerate cost preview:', error);
    throw error;
  }
};

/**
 * Preview the credit cost for regenerating an entire campaign
 * @param {Array} adTypes - List of ad types to regenerate
 * @param {number} numPersonas - Number of personas
 * @param {string} imageModel - Image model to use
 * @param {string} nanoBananaResolution - Resolution for nano banana
 * @param {string} sessionToken - User's session token
 * @param {Object} productData - Optional product data for complete request
 * @returns {Promise<Object>} Credit preview data
 */
export const previewCampaignRegenerateCost = async (adTypes, numPersonas, imageModel, nanoBananaResolution, sessionToken, productData = null) => {
  try {
    // The /preview-credit-cost endpoint expects AdGenerationRequest format
    const requestBody = {
      product: productData || {
        product_name: 'Campaign Regeneration',
        product_description: 'Regenerating campaign content',
        key_benefits: '',
        brand_voice: 'professional',
        age_range_min: 18,
        age_range_max: 65,
        gender: 'All',
        geography: 'India',
        enable_personas: numPersonas > 1,
        persona_count: numPersonas > 1 ? String(numPersonas) : '1',
        image_model: imageModel || 'nebius',
        nano_banana_resolution: nanoBananaResolution || '4K'
      },
      ad_types: adTypes
    };
    
    const response = await axios.post(
      `${API}/preview-credit-cost`,
      requestBody,
      { headers: { Authorization: `Bearer ${sessionToken}` } }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching campaign regenerate cost preview:', error);
    throw error;
  }
};

/**
 * Format credit info for display in confirmation modal
 * @param {Object} creditPreview - Credit preview response from API
 * @returns {string} Formatted credit info string
 */
export const formatCreditInfo = (creditPreview) => {
  if (!creditPreview) return '';
  
  const { cost_breakdown, current_balance, can_generate, is_power_user, shortfall, video_time_estimate } = creditPreview;
  
  if (is_power_user) {
    let info = '\n\n⭐ **Power User** - No credits will be charged';
    if (video_time_estimate) {
      info += `\n\n⏱️ **Estimated Time:** ${video_time_estimate}`;
    }
    return info;
  }
  
  const netCost = cost_breakdown?.net_cost || 0;
  const freeDiscount = cost_breakdown?.free_tier_discount?.total_credits || 0;
  
  let creditInfo = '';
  
  if (freeDiscount > 0) {
    creditInfo = `\n\n💰 **Credit Cost:** ${netCost} credits (Free tier applied! 🎁)\n📊 **Your Balance:** ${current_balance} credits`;
  } else {
    creditInfo = `\n\n💰 **Credit Cost:** ${netCost} credits\n📊 **Your Balance:** ${current_balance} credits`;
  }
  
  // Add video time estimate if present
  if (video_time_estimate) {
    creditInfo += `\n\n⏱️ **Estimated Time:** ${video_time_estimate}`;
  }
  
  if (!can_generate) {
    creditInfo += `\n\n⚠️ **Insufficient Credits!** You need ${shortfall} more credits.`;
  } else {
    creditInfo += `\n✅ **After regeneration:** ${current_balance - netCost} credits`;
  }
  
  return creditInfo;
};

/**
 * Regenerate a specific ad within a campaign
 * @param {string} campaignId - The campaign ID
 * @param {Object} regenerateData - Regeneration parameters
 * @param {string} sessionToken - User's session token
 * @returns {Promise<Object>} Updated campaign data
 */
export const regenerateAd = async (campaignId, regenerateData, sessionToken) => {
  try {
    const response = await axios.post(
      `${API}/campaigns/${campaignId}/regenerate-ad`,
      regenerateData,
      {
        headers: { Authorization: `Bearer ${sessionToken}` },
        // Image regen with GPT Image 2 can take 2-3 minutes (Kie AI polling).
        // Set a generous 6-minute timeout to avoid premature client errors.
        timeout: 360000,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error regenerating ad:', error);
    throw error;
  }
};

/**
 * Get credit cost display name for ad types
 * @param {string} adType - The ad type
 * @returns {string} Human-readable ad type name
 */
export const getAdTypeDisplayName = (adType) => {
  const displayNames = {
    'whatsapp_ad': 'WhatsApp Message',
    'social_media': 'Social Media Post',
    'facebook_ad': 'Facebook Ad',
    'instagram_ad': 'Instagram Ad',
    'linkedin_ad': 'LinkedIn Ad',
    'tiktok_script': 'TikTok Script',
    'telegram_ad': 'Telegram Ad',
    'google_ads': 'Google Ads',
    'image_promo': 'Image Ad',
    'video_promo': 'Video Ad',
    'video_ad': 'Video Ad',
    'email_marketing': 'Email Marketing',
    'product_description': 'Product Description'
  };
  return displayNames[adType] || adType.replace('_', ' ');
};

/**
 * Get credit category for ad type
 * @param {string} adType - The ad type
 * @returns {string} Category (text, image, video)
 */
export const getAdTypeCategory = (adType) => {
  const imageTypes = ['image_promo', 'telegram_ad', 'facebook_ad_image', 'instagram_image'];
  const videoTypes = ['video_promo', 'video_ad', 'tiktok_video', 'youtube_short'];
  
  if (videoTypes.includes(adType)) return 'video';
  if (imageTypes.includes(adType)) return 'image';
  return 'text';
};

/**
 * Get credit cost emoji based on ad category
 * @param {string} category - Ad category (text, image, video)
 * @returns {string} Emoji for the category
 */
export const getCategoryEmoji = (category) => {
  const emojis = {
    'text': '📝',
    'image': '🖼️',
    'video': '🎬'
  };
  return emojis[category] || '✨';
};

export default {
  fetchFullCampaignData,
  previewRegenerateCost,
  previewCampaignRegenerateCost,
  formatCreditInfo,
  regenerateAd,
  getAdTypeDisplayName,
  getAdTypeCategory,
  getCategoryEmoji
};
